"use client";

import { Share2 } from "lucide-react";
import { announce } from "@/lib/announce";
import { play } from "cuelume";

interface ShareButtonProps {
  title: string;
  url?: string;
}

export function ShareButton({ title, url }: ShareButtonProps) {
  const shareUrl = url ?? (typeof window !== "undefined" ? window.location.href : "");

  async function handleShare() {
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title, url: shareUrl });
        play("success");
        return;
      } catch (err) {
        // AbortError means the user cancelled — don't show an error toast
        if (err instanceof Error && err.name === "AbortError") return;
      }
    }

    // Clipboard fallback
    try {
      await navigator.clipboard.writeText(shareUrl);
      play("chime");
      announce("Link copied to clipboard");
    } catch {
      play("error");
      announce("Could not copy link");
    }
  }

  return (
    <button
      type="button"
      aria-label="Share article"
      onClick={handleShare}
      data-cuelume-press="press"
      data-cuelume-release="scan"
      className="flex items-center gap-2 px-4 py-2 rounded-[var(--radius-pill)] text-[var(--tls-fg-muted)] transition-colors hover:bg-[var(--tls-surface-elevated)] hover:text-[var(--tls-fg-strong)] tlsr-focus"
    >
      <Share2 className="size-4" aria-hidden="true" />
      <span className="text-sm">Share</span>
    </button>
  );
}
